import { AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { ResponsiveService } from '@services/responsive.service';
import { NavigationError, Router } from '@angular/router';
import { SnackbarService } from '@services/snackbar.service';
import { PageTitleService } from '@services/page-title.service';

declare global {
  function particlesJS(id: string, config: any): void;
}

@Component({
  selector: 'novus-sidebar-layout',
  templateUrl: './sidebar-layout.component.html',
  styleUrls: ['./sidebar-layout.component.css'],
  standalone: false
})
export class SidebarLayoutComponent implements OnDestroy, AfterViewInit {
  opened: boolean;
  mobile$ = inject(ResponsiveService).isMobileEvent;
  screenWidth() { return window.innerWidth };
  router = inject(Router);
  pageTitle = inject(PageTitleService);
  snackbar = inject(SnackbarService);
  collapsed = signal<boolean>(false);
  routeTarget = computed(() => {
    const target = this.pageTitle.routeTo();
    if (target === false) return null;
    if (target === true) return '/apps/projects';
    return target;
  })
  isNavigating = computed(() => !!this.router.currentNavigation());
  @ViewChild('particles') particles!: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;

  constructor() {
    this.opened = false;

    this.router.events.subscribe(event => {
      if (event instanceof NavigationError)
        this.snackbar.open(event.error);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => particlesJS("particles", this.getParticlesConfig()));
    const particles = this.particles.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      window.dispatchEvent(new Event('resize'));
    });
    this.resizeObserver.observe(particles.parentElement!);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  toggleSidenav() {
    this.opened = !this.opened;
  }

  closeSidebar() {
    this.opened = false;
  }

  private getParticlesConfig() {
    return {
      particles: {
        number: { value: 40, density: { enable: true, value_area: 300 } },
        color: { value: "#707070" },
        shape: {
          type: "circle",
          stroke: { width: 0, color: "rgba(227, 227, 227,.75)" },
          polygon: { nb_sides: 3 },
          image: { src: "img/github.svg", width: 100, height: 100 }
        },
        opacity: {
          value: 1,
          random: false,
          anim: { enable: false, speed: 1, opacity_min: 0.1, sync: false }
        },
        size: {
          value: 5,
          random: false,
          anim: { enable: false, speed: 40, size_min: 0.1, sync: false }
        },
        line_linked: {
          enable: true,
          distance: 200,
          color: "#707070",
          opacity: 1,
          width: 3
        },
        move: {
          enable: true,
          speed: 1,
          direction: "none",
          random: false,
          straight: false,
          out_mode: "out",
          bounce: false,
          attract: { enable: false, rotateX: 600, rotateY: 1200 }
        }
      },
      interactivity: {
        detect_on: "canvas",
        events: {
          onhover: { enable: false, mode: "repulse" },
          onclick: { enable: false, mode: "push" },
          resize: true
        },
        modes: {
          grab: { distance: 400, line_linked: { opacity: 1 } },
          bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 },
          repulse: { distance: 200, duration: 0.4 },
          push: { particles_nb: 4 },
          remove: { particles_nb: 2 }
        }
      },
      retina_detect: true
    };
  }

}
